import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../utils/helpers";

export const canonicalTopicSpace = state => state.space?.mirrorOf || state.spaceId || "";
const selectionInScope = state => state.topicSpaceId === canonicalTopicSpace(state)
  && (!state.topicSourceInstanceId || state.topicSourceInstanceId === state.sourceInstanceId);
export const selectedTopicId = state => selectionInScope(state) ? state.topicId || undefined : undefined;
export const selectedTopicSelection = state => selectionInScope(state) ? state.topicSelection || (state.topicId ? "topic" : undefined) : undefined;
export const topicSelectionPayload = state => selectedTopicSelection(state) === "default" ? { topicSelection: "default", topicId: null }
  : selectedTopicId(state) ? { topicSelection: "topic", topicId: selectedTopicId(state) } : {};
export const validTopicSettings = settings => !!settings && typeof settings.requireTopicSelection === "boolean"
  && Number.isSafeInteger(settings.revision) && settings.revision >= 0
  && Number.isSafeInteger(settings.activeTopicCount) && settings.activeTopicCount >= 0;
export const validTopic = topic => !!topic && typeof topic.id === "string" && typeof topic.name === "string"
  && Number.isInteger(topic.revision) && topic.revision > 0 && typeof topic.archived === "boolean";

// Reads are scoped to the canonical space. Wizard reads never publish changes.
export default function useSpaceTopics(spaceId, enabled = true, readOnly = false) {
  const generation = useRef(0);
  const controllerRef = useRef(null);
  const [state, setState] = useState({ spaceId: null, loading: true, topics: [], canManage: false, error: null });
  const read = useCallback(async (fresh = false) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const current = ++generation.current;
    if (!spaceId || !enabled) return;
    setState(previous => ({ ...previous, topics: previous.spaceId === spaceId ? previous.topics : [], settings: previous.spaceId === spaceId ? previous.settings : undefined, spaceId, loading: true, error: null, canManage: false }));
    try {
      const result = await api(`/api/cicd/topics?spaceId=${encodeURIComponent(spaceId)}${readOnly ? "&readOnly=1" : ""}${readOnly && fresh ? "&refresh=1" : ""}`, { cache: "no-store", signal: controller.signal, ...(readOnly ? { retry: false, timeout: 10000 } : {}) });
      if (result?.spaceId !== spaceId || !validTopicSettings(result?.settings) || !Array.isArray(result?.topics) || !result.topics.every(topic => validTopic(topic) && (topic.selectable === undefined || typeof topic.selectable === "boolean")) || typeof result.canManage !== "boolean") {
        throw Object.assign(new Error("TOPIC_RESPONSE_INVALID"), { code: "TOPIC_RESPONSE_INVALID" });
      }
      if (result.sync?.state === "stale" && !result.topics.some(topic => !topic.archived && topic.selectable !== false)) {
        throw Object.assign(new Error("TOPIC_READ_FAILED"), { code: result.sync.code || "TOPIC_READ_FAILED" });
      }
      if (current === generation.current) setState({ spaceId, loading: false, topics: result.topics, canManage: result.canManage, settings: result.settings, sync: result.sync, error: null });
    } catch (error) {
      if (current === generation.current) setState(previous => ({ ...previous, spaceId, loading: false, canManage: false, error }));
    }
  }, [spaceId, enabled, readOnly]);
  const reload = useCallback(() => read(true), [read]);
  useEffect(() => { read(); return () => { generation.current += 1; controllerRef.current?.abort(); }; }, [read]);
  return state.spaceId === spaceId && enabled ? { ...state, reload }
    : { topics: [], canManage: false, loading: !!spaceId && enabled, error: null, reload };
}
